import { useEffect, useRef, useState, useCallback } from 'react';
import { useStore } from '../stores/editor.store';
import { EditorViewport } from '../components/Editor/EditorViewport';
import { CreatorCopilot } from '../components/Editor/CreatorCopilot';
import { Scene3D } from '../3D/Scene3D';
import { SceneObject } from '../types';
import { Game, emptyGame, emptyScene } from '../types/game';
import { BEHAVIOR_CATALOG, hasBehavior, getBehavior, toggleBehavior, setBehaviorParam } from '../game/behaviors';
import { getTemplate, GAME_TEMPLATES } from '../game/templates';
import { ITEM_PRESETS, ItemPreset } from '../game/presets';
import { FIGURES, instantiateFigure } from '../game/figures';
import { MODEL_ASSETS, ModelAsset } from '../game/assets';
import TutorialOverlay from '../components/Editor/TutorialOverlay';
import { integration } from '../integration';
import api, { modelAPI } from '../services/api';

type Vec3 = { x: number; y: number; z: number };

// Unified library categories (order shown in the palette).
const CATEGORY_ORDER = [
  'Characters',
  'Environment',
  'Structures',
  'Props',
  'Collectibles',
  'Blocks',
];

// Map the code-provided presets and figures into the taxonomy so they live in
// the same category-grouped library as uploaded models.
const PRESET_CATEGORY: Record<string, string> = {
  player: 'Characters',
  coin: 'Collectibles',
  gem: 'Collectibles',
  goal: 'Collectibles',
  crate: 'Props',
  hazard: 'Props',
  rock: 'Environment',
  bush: 'Environment',
  wall: 'Structures',
  floor: 'Structures',
  pillar: 'Structures',
};
const FIGURE_CATEGORY: Record<string, string> = {
  hero: 'Characters',
  robot: 'Characters',
  tree: 'Environment',
};

type PaletteItem = {
  key: string;
  label: string;
  category: string;
  swatch: string;
  hint?: string;
  add: () => void;
};

function VectorField({
  label,
  value,
  onChange,
  step = 0.5,
}: {
  label: string;
  value: Vec3;
  onChange: (v: Vec3) => void;
  step?: number;
}) {
  const set = (axis: keyof Vec3, raw: string) => {
    const n = parseFloat(raw);
    onChange({ ...value, [axis]: Number.isFinite(n) ? n : 0 });
  };
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <div className="grid grid-cols-3 gap-2">
        {(['x', 'y', 'z'] as const).map((axis) => (
          <input
            key={axis}
            type="number"
            step={step}
            value={value[axis]}
            onChange={(e) => set(axis, e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded px-1.5 py-1 text-xs text-white"
          />
        ))}
      </div>
    </div>
  );
}

export default function EditorPage() {
  const sceneRef = useRef<Scene3D | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  // Model palette: starts with the bundled fallback list, then replaced by the
  // live registry from the backend once it loads (so new models need no redeploy).
  const [models, setModels] = useState<ModelAsset[]>(MODEL_ASSETS);
  const [leftTab, setLeftTab] = useState<'add' | 'outline'>('add');
  const [activeCat, setActiveCat] = useState<string>('All');
  const [assetSearch, setAssetSearch] = useState('');
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [importNote, setImportNote] = useState(false);

  useEffect(() => {
    let cancelled = false;
    modelAPI
      .getAll()
      .then((res) => {
        const data = res.data?.data;
        if (!cancelled && res.data?.success && Array.isArray(data) && data.length) {
          setModels(
            data.map((m) => ({
              id: String(m.id),
              label: m.label,
              src: m.url,
              category: m.category,
              defaultBehavior: m.defaultBehavior,
              fitHeight: m.fitHeight,
              scale: m.scale,
            }))
          );
        }
      })
      .catch(() => {
        /* backend unavailable — keep the bundled fallback list */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadGame = useStore((s) => s.loadGame);
  const setGameMeta = useStore((s) => s.setGameMeta);
  const gameTitle = useStore((s) => s.game.title);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const selectedObjectId = useStore((s) => s.selectedObjectId);
  const updateObject = useStore((s) => s.updateObject);
  const removeObject = useStore((s) => s.removeObject);
  const selectObject = useStore((s) => s.selectObject);
  const addObject = useStore((s) => s.addObject);
  const scenes = useStore((s) => s.game.scenes);
  const activeSceneId = useStore((s) => s.activeSceneId);
  const loadScene = useStore((s) => s.loadScene);
  const commitSceneObjects = useStore((s) => s.commitSceneObjects);
  const addScene = useStore((s) => s.addScene);
  const deleteScene = useStore((s) => s.deleteScene);
  const setActiveSceneId = useStore((s) => s.setActiveSceneId);

  const selected = sceneObjects.find((o) => o.uuid === selectedObjectId) || null;

  // Load (or create) the project on mount
  useEffect(() => {
    const loadProject = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        let id = params.get('project');
        const templateId = params.get('template');

        // Start from a template: create a new project pre-filled and saved.
        if (!id && templateId) {
          const tpl = getTemplate(templateId);
          if (tpl) {
            const created = await api.post('/projects', { name: tpl.title });
            id = String(created.data.data.id);
            const tplGame = tpl.build(id);
            await api.put(`/projects/${id}`, { scene: tplGame, name: tplGame.title });
            window.history.replaceState(null, '', `?project=${id}`);
            loadGame(tplGame);
            setProjectId(id);
            setLoading(false);
            return;
          }
        }

        if (!id) {
          const response = await api.post('/projects', { name: 'New Project' });
          id = String(response.data.data.id);
          window.history.replaceState(null, '', `?project=${id}`);
        }

        try {
          const projectRes = await api.get(`/projects/${id}`);
          const data = projectRes.data?.data;
          const stored = data?.scene;
          if (stored && !Array.isArray(stored) && Array.isArray(stored.scenes)) {
            // stored value is already a full Game
            loadGame(stored as Game);
          } else {
            // legacy: stored value is an array of objects (or empty)
            const objects = Array.isArray(stored) ? stored : [];
            loadGame(
              emptyGame({
                id: String(id),
                title: data?.name || 'Untitled Game',
                description: data?.description,
                scenes: [emptyScene({ objects })],
              })
            );
          }
        } catch {
          loadGame(emptyGame({ id: String(id) }));
        }

        setProjectId(id);
        setLoading(false);
      } catch (err: any) {
        setError('Failed to load project: ' + (err.message || 'Unknown error'));
        setLoading(false);
      }
    };
    loadProject();
  }, [loadGame]);

  const assembleGame = useCallback((): Game => {
    const base = useStore.getState().getGame();
    const activeId = useStore.getState().activeSceneId;
    const liveObjects = sceneRef.current?.getAllObjects() ?? [];
    return {
      ...base,
      scenes: base.scenes.map((s) =>
        s.id === activeId ? { ...s, objects: liveObjects } : s
      ),
    };
  }, []);

  const handleSave = useCallback(async () => {
    if (!projectId || !sceneRef.current) return;
    try {
      setSaving(true);
      const game = assembleGame();
      await api.put(`/projects/${projectId}`, { scene: game, name: game.title });
      setLastSaved(new Date());
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  }, [projectId, assembleGame]);

  useEffect(() => {
    if (!projectId) return;
    const interval = setInterval(() => {
      handleSave();
    }, 30000);
    return () => clearInterval(interval);
  }, [projectId, handleSave]);

  // Apply an edit to both the store (UI/persistence) and the 3D scene (live)
  const applyUpdate = (updates: Partial<SceneObject>) => {
    if (!selectedObjectId) return;
    updateObject(selectedObjectId, updates);
    sceneRef.current?.updateObject(selectedObjectId, updates);
  };

  const handleDeleteSelected = () => {
    if (!selectedObjectId) return;
    sceneRef.current?.removeObject(selectedObjectId);
    removeObject(selectedObjectId);
  };

  const handleExport = async () => {
    try {
      const game = assembleGame();
      const identity = await integration.identity.getCurrentIdentity();
      const result = await integration.publish.publish(game, identity);
      if (result.url) {
        const a = document.createElement('a');
        a.href = result.url;
        a.download = result.details || 'game.json';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(result.url);
      }
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const switchScene = (targetId: string) => {
    if (targetId === activeSceneId) return;
    const live = sceneRef.current?.getAllObjects() ?? [];
    commitSceneObjects(activeSceneId, live);
    const target = scenes.find((s) => s.id === targetId);
    const objs = target?.objects ?? [];
    setActiveSceneId(targetId);
    loadScene(objs);
    sceneRef.current?.loadObjects(objs);
  };

  const handleAddShape = (type: 'cube' | 'sphere' | 'plane') => {
    const scene = sceneRef.current;
    if (!scene) return;
    const obj = scene.addObject(type);
    addObject(obj);
    selectObject(obj.uuid);
  };

  const handleAddPreset = (preset: ItemPreset) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const obj = scene.addObject(preset.type as 'cube' | 'sphere' | 'plane');
    const styled: SceneObject = {
      ...obj,
      name: preset.baseName,
      color: preset.color,
      scale: preset.scale,
      rotation: preset.rotation ?? obj.rotation,
      behaviors: preset.behaviors,
    };
    scene.updateObject(obj.uuid, {
      color: styled.color,
      scale: styled.scale,
      rotation: styled.rotation,
      name: styled.name,
      behaviors: styled.behaviors,
    });
    addObject(styled);
    selectObject(obj.uuid);
  };

  const handleAddFigure = (figureId: string) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const specs = instantiateFigure(figureId);
    if (!specs || specs.length === 0) return;
    scene.addComposite(specs);
    specs.forEach((o) => addObject(o));
    selectObject(specs[0].uuid);
  };

  const handleAddModel = (asset: ModelAsset) => {
    const scene = sceneRef.current;
    if (!scene) return;
    const c = (globalThis as any).crypto;
    const uuid =
      c && typeof c.randomUUID === 'function'
        ? c.randomUUID()
        : `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const sc = asset.scale ?? 1;
    const behaviors = asset.defaultBehavior
      ? [{ type: asset.defaultBehavior, params: {} as Record<string, unknown> }]
      : [];
    const spec: SceneObject = {
      uuid,
      name: asset.label,
      type: 'model',
      src: asset.src,
      fitHeight: asset.fitHeight,
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: sc, y: sc, z: sc },
      color: '#ffffff',
      behaviors,
    };
    scene.addComposite([spec]);
    addObject(spec);
    selectObject(uuid);
  };

  const handleApplyTemplate = (templateId: string) => {
    const scene = sceneRef.current;
    const tpl = getTemplate(templateId);
    if (!scene || !tpl || !projectId) return;
    if (!window.confirm('Replace the current scene with this template?')) return;
    const game = tpl.build(projectId);
    const objs = game.scenes[0]?.objects ?? [];
    loadScene(objs);
    scene.loadObjects(objs);
    commitSceneObjects(activeSceneId, objs);
    setShowTemplates(false);
  };

  const handleAddScene = () => {
    const live = sceneRef.current?.getAllObjects() ?? [];
    commitSceneObjects(activeSceneId, live);
    const id = addScene();
    setActiveSceneId(id);
    loadScene([]);
    sceneRef.current?.loadObjects([]);
  };

  const handleDeleteScene = (id: string) => {
    if (scenes.length <= 1) return;
    if (id === activeSceneId) {
      const other = scenes.find((s) => s.id !== id);
      if (other) {
        setActiveSceneId(other.id);
        loadScene(other.objects);
        sceneRef.current?.loadObjects(other.objects);
      }
    }
    deleteScene(id);
  };

  if (loading) {
    return (
      <div className="w-full h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="text-2xl font-bold mb-4">Loading Game Studio...</div>
          <div className="animate-spin inline-block">⚙️</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="text-2xl font-bold text-red-500 mb-4">Error</div>
          <div className="text-red-400">{error}</div>
        </div>
      </div>
    );
  }

  const paletteItems: PaletteItem[] = [
    { key: 'shape-cube', label: 'Cube', category: 'Blocks', swatch: '#9cd2ae', add: () => handleAddShape('cube') },
    { key: 'shape-sphere', label: 'Sphere', category: 'Blocks', swatch: '#85b7eb', add: () => handleAddShape('sphere') },
    { key: 'shape-plane', label: 'Plane', category: 'Blocks', swatch: '#cbd5e1', add: () => handleAddShape('plane') },
    ...ITEM_PRESETS.map((p) => ({
      key: 'preset-' + p.id,
      label: p.label,
      category: PRESET_CATEGORY[p.id] ?? 'Props',
      swatch: p.color,
      hint: p.behaviors?.[0]?.type,
      add: () => handleAddPreset(p),
    })),
    ...FIGURES.map((f) => ({
      key: 'figure-' + f.id,
      label: f.label,
      category: FIGURE_CATEGORY[f.id] ?? 'Characters',
      swatch: '#7c77dd',
      add: () => handleAddFigure(f.id),
    })),
    ...models.map((m) => ({
      key: 'model-' + m.id,
      label: m.label,
      category: (m.category as string) ?? 'Props',
      swatch: '#5dcaa5',
      hint: m.defaultBehavior,
      add: () => handleAddModel(m),
    })),
  ];

  const paletteButton = (i: PaletteItem) => (
    <button
      key={i.key}
      onClick={i.add}
      title={`Add ${i.label}${i.hint ? ` (${i.hint})` : ''}`}
      className="flex items-center gap-2 px-2 py-1.5 rounded bg-gray-900 border border-gray-700 text-gray-200 text-xs hover:border-emerald-500"
    >
      <span className="inline-block w-3 h-3 rounded-sm flex-none" style={{ backgroundColor: i.swatch }} />
      <span className="truncate">{i.label}</span>
    </button>
  );

  return (
    <div className="w-full h-screen flex flex-col bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="flex items-center" aria-label="Home">
            <img src="/yip-logo.png" alt="YIP" className="h-7 w-7" />
          </a>
          <a
            href="/projects"
            className="text-gray-400 hover:text-white text-sm whitespace-nowrap"
          >
            ← Projects
          </a>
          <div>
            <input
              type="text"
              value={gameTitle}
              onChange={(e) => setGameMeta({ title: e.target.value })}
              className="bg-transparent text-white text-2xl font-bold border-b border-transparent hover:border-gray-600 focus:border-blue-500 focus:outline-none max-w-xs"
              aria-label="Game title"
            />
            <p className="text-gray-400 text-sm">YIP Game Studio</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-tutorial="play"
            onClick={() =>
              projectId &&
              window.open(`/play?project=${projectId}&from=editor`, '_blank')
            }
            className="bg-blue-600 hover:bg-blue-500 text-white font-semibold px-4 py-2 rounded"
          >
            Play ▶
          </button>
          <button
            onClick={handleExport}
            className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded"
          >
            Export
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-semibold px-4 py-2 rounded"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      {/* Main editor area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Object list / hierarchy */}
        <div className="w-56 bg-gray-800 border-r border-gray-700 p-3 overflow-y-auto">
          {/* Scenes */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-bold text-sm">Scenes</h3>
              <button
                onClick={handleAddScene}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                + Add
              </button>
            </div>
            <div className="space-y-1">
              {scenes.map((s) => (
                <div
                  key={s.id}
                  className={
                    'flex items-center rounded ' +
                    (s.id === activeSceneId
                      ? 'bg-gray-700'
                      : 'hover:bg-gray-700/50')
                  }
                >
                  <button
                    onClick={() => switchScene(s.id)}
                    className="flex-1 text-left px-2 py-1 text-xs text-gray-200 truncate"
                  >
                    {s.name}
                  </button>
                  {scenes.length > 1 && (
                    <button
                      onClick={() => handleDeleteScene(s.id)}
                      className="text-gray-500 hover:text-red-400 px-1.5 text-xs"
                      aria-label="Delete scene"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bring content in */}
          <div className="flex gap-2 mb-2">
            <button
              data-tutorial="templates"
              onClick={() => { setShowTemplates((v) => !v); setImportNote(false); }}
              className="flex-1 px-2 py-2 rounded bg-gray-900 border border-gray-700 text-gray-200 text-xs hover:border-purple-500"
            >
              Templates
            </button>
            <button
              data-tutorial="import"
              onClick={() => { setImportNote((v) => !v); setShowTemplates(false); }}
              className="flex-1 px-2 py-2 rounded bg-gray-900 border border-gray-700 text-gray-200 text-xs hover:border-emerald-500"
            >
              Import
            </button>
          </div>

          {showTemplates && (
            <div className="mb-3 rounded border border-gray-700 bg-gray-900 p-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                Start from a template
              </div>
              <div className="space-y-1">
                {GAME_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => handleApplyTemplate(t.id)}
                    className="w-full text-left px-2 py-1.5 rounded text-xs text-gray-200 hover:bg-gray-700"
                  >
                    {t.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {importNote && (
            <div className="mb-3 rounded border border-emerald-700/50 bg-emerald-900/20 p-2 text-[11px] text-emerald-200 leading-relaxed">
              Bring in your own models is coming soon — once asset storage is
              wired up you'll be able to import your own .glb files here.
              <button
                onClick={() => setImportNote(false)}
                className="block mt-1 text-emerald-400 hover:text-emerald-300"
              >
                Got it
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-gray-700 mb-3">
            <button
              onClick={() => setLeftTab('add')}
              className={
                'flex-1 text-xs py-2 ' +
                (leftTab === 'add'
                  ? 'text-white border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-300')
              }
            >
              Add
            </button>
            <button
              onClick={() => setLeftTab('outline')}
              className={
                'flex-1 text-xs py-2 ' +
                (leftTab === 'outline'
                  ? 'text-white border-b-2 border-blue-500'
                  : 'text-gray-500 hover:text-gray-300')
              }
            >
              Outline
            </button>
          </div>

          {leftTab === 'add' ? (
            <div data-tutorial="add-library">
              <input
                type="text"
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                placeholder="Search assets"
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-white mb-2"
              />
              <div className="flex flex-wrap gap-1 mb-3">
                {['All', ...CATEGORY_ORDER].map((c) => (
                  <button
                    key={c}
                    onClick={() => setActiveCat(c)}
                    className={
                      'text-[10px] px-2 py-0.5 rounded-full border transition-colors ' +
                      (activeCat === c
                        ? 'border-blue-500 bg-blue-500/20 text-blue-200'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500')
                    }
                  >
                    {c}
                  </button>
                ))}
              </div>
              {(() => {
                const q = assetSearch.trim().toLowerCase();
                let items = paletteItems;
                if (activeCat !== 'All') items = items.filter((i) => i.category === activeCat);
                if (q) items = items.filter((i) => i.label.toLowerCase().includes(q));
                if (items.length === 0) {
                  return <p className="text-gray-500 text-xs">No matching assets.</p>;
                }
                if (activeCat !== 'All' || q) {
                  return (
                    <div className="grid grid-cols-2 gap-1.5">
                      {items.map((i) => paletteButton(i))}
                    </div>
                  );
                }
                const present = Array.from(new Set(items.map((i) => i.category)));
                const ordered = [
                  ...CATEGORY_ORDER.filter((c) => present.includes(c)),
                  ...present.filter((c) => !CATEGORY_ORDER.includes(c)),
                ];
                return ordered.map((cat) => {
                  const inCat = items.filter((i) => i.category === cat);
                  if (!inCat.length) return null;
                  return (
                    <div key={cat} className="mb-3">
                      <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                        {cat}
                      </div>
                      <div className="grid grid-cols-2 gap-1.5">
                        {inCat.map((i) => paletteButton(i))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div data-tutorial="outline">
              {sceneObjects.length === 0 ? (
                <p className="text-gray-400 text-xs">
                  No objects yet. Add one from the Add tab.
                </p>
              ) : (
                <ul className="space-y-1">
                  {sceneObjects.map((obj) => (
                    <li key={obj.uuid}>
                      <button
                        onClick={() => selectObject(obj.uuid)}
                        style={obj.parentId ? { paddingLeft: 22 } : undefined}
                        className={
                          'w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 transition-colors ' +
                          (obj.uuid === selectedObjectId
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-300 hover:bg-gray-700')
                        }
                      >
                        <span className="inline-block w-4 text-center text-xs opacity-70">
                          {obj.type === 'cube'
                            ? '▣'
                            : obj.type === 'sphere'
                            ? '●'
                            : obj.type === 'model'
                            ? '◆'
                            : '▬'}
                        </span>
                        <span className="truncate">{obj.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 bg-gray-950">
          <EditorViewport sceneRef={sceneRef} />
        </div>

        {/* Properties panel */}
        <div className="w-72 bg-gray-800 border-l border-gray-700 p-4 overflow-y-auto">
          <h3 className="text-white font-bold mb-4">Properties</h3>

          {!selected ? (
            <p className="text-gray-400 text-xs">
              Select an object in the viewport to edit its properties.
            </p>
          ) : (
            <div className="space-y-4 text-white text-sm">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Name</label>
                <input
                  type="text"
                  value={selected.name}
                  onChange={(e) => applyUpdate({ name: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Type</label>
                <div className="text-gray-300 capitalize">{selected.type}</div>
              </div>

              <div>
                <label className="block text-xs text-gray-400 mb-1">Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selected.color}
                    onChange={(e) => applyUpdate({ color: e.target.value })}
                    className="h-8 w-10 bg-gray-900 border border-gray-700 rounded cursor-pointer"
                  />
                  <span className="text-xs text-gray-400">{selected.color}</span>
                </div>
              </div>

              <VectorField
                label="Position"
                value={selected.position}
                onChange={(v) => applyUpdate({ position: v })}
              />
              <VectorField
                label="Rotation (radians)"
                value={selected.rotation}
                step={0.1}
                onChange={(v) => applyUpdate({ rotation: v })}
              />
              <VectorField
                label="Scale"
                value={selected.scale}
                step={0.1}
                onChange={(v) => applyUpdate({ scale: v })}
              />

              {/* Behaviors — turn this object into gameplay (runs in Play mode) */}
              <div data-tutorial="behaviors">
                <label className="block text-xs text-gray-400 mb-2">
                  Behaviors
                </label>
                <div className="flex flex-wrap gap-2">
                  {BEHAVIOR_CATALOG.map((b) => {
                    const active = hasBehavior(selected, b.type);
                    return (
                      <button
                        key={b.type}
                        onClick={() =>
                          applyUpdate({
                            behaviors: toggleBehavior(selected.behaviors, b.type),
                          })
                        }
                        title={b.description}
                        className={
                          'px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ' +
                          (active
                            ? 'bg-purple-600/30 border-purple-500 text-purple-100'
                            : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-500')
                        }
                      >
                        {b.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                  Behaviors make objects do things in Play mode — tag one object{' '}
                  <span className="text-gray-300">Player</span>, others{' '}
                  <span className="text-gray-300">Collectible</span>, and one{' '}
                  <span className="text-gray-300">Goal</span>.
                </p>

                {/* Editable settings for whichever behaviors are active */}
                {BEHAVIOR_CATALOG.filter(
                  (b) => hasBehavior(selected, b.type) && b.params.length > 0
                ).map((b) => (
                  <div key={b.type} className="mt-3 rounded border border-gray-700 bg-gray-900/60 p-2">
                    <div className="text-[10px] uppercase tracking-wide text-purple-300/80 mb-1.5">
                      {b.label} settings
                    </div>
                    <div className="space-y-1.5">
                      {b.params.map((spec) => {
                        const cur = getBehavior(selected, b.type)?.params?.[spec.key];
                        if (spec.type === 'boolean') {
                          const val =
                            typeof cur === 'boolean' ? cur : (spec.default as boolean);
                          return (
                            <label
                              key={spec.key}
                              className="flex items-center justify-between text-xs text-gray-300"
                            >
                              <span className="truncate pr-2">{spec.label}</span>
                              <input
                                type="checkbox"
                                checked={val}
                                onChange={(e) =>
                                  applyUpdate({
                                    behaviors: setBehaviorParam(
                                      selected.behaviors,
                                      b.type,
                                      spec.key,
                                      e.target.checked
                                    ),
                                  })
                                }
                              />
                            </label>
                          );
                        }
                        const val =
                          typeof cur === 'number' ? cur : (spec.default as number);
                        return (
                          <label
                            key={spec.key}
                            className="flex items-center justify-between gap-2 text-xs text-gray-300"
                          >
                            <span className="truncate pr-2">{spec.label}</span>
                            <input
                              type="number"
                              value={val}
                              min={spec.min}
                              max={spec.max}
                              step={spec.step}
                              onChange={(e) => {
                                let n = parseFloat(e.target.value);
                                if (Number.isNaN(n)) return;
                                if (spec.min !== undefined) n = Math.max(spec.min, n);
                                if (spec.max !== undefined) n = Math.min(spec.max, n);
                                applyUpdate({
                                  behaviors: setBehaviorParam(
                                    selected.behaviors,
                                    b.type,
                                    spec.key,
                                    n
                                  ),
                                });
                              }}
                              className="w-20 bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white text-right"
                            />
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleDeleteSelected}
                className="w-full bg-red-600 hover:bg-red-500 rounded px-3 py-2 text-sm font-semibold"
              >
                Delete Object
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Creator Copilot (collapsible) */}
      <div className="border-t border-gray-700 bg-gray-800">
        {copilotOpen ? (
          <>
            <button
              onClick={() => setCopilotOpen(false)}
              className="w-full flex items-center justify-end gap-1 px-4 py-1 text-xs text-gray-400 hover:text-white"
            >
              Collapse ▾
            </button>
            <CreatorCopilot sceneRef={sceneRef} />
          </>
        ) : (
          <button
            onClick={() => setCopilotOpen(true)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs text-purple-300 hover:bg-gray-700"
          >
            <span>
              Creator Copilot{' '}
              <span className="text-gray-500">— describe a scene to build it</span>
            </span>
            <span className="text-gray-500">▸</span>
          </button>
        )}
      </div>
      <TutorialOverlay />

      {/* Status bar */}
      <div className="bg-gray-800 border-t border-gray-700 px-4 py-2 text-xs text-gray-400 flex justify-between">
        <span>
          {selected ? `Selected: ${selected.name}` : 'Click an object to select it.'}
        </span>
        <span>
          {saving
            ? 'Saving…'
            : lastSaved
            ? `Last saved ${lastSaved.toLocaleTimeString()}`
            : 'Not saved yet'}
        </span>
      </div>
    </div>
  );
}
